import { compact, isEmpty, isEqual, omitBy, uniq } from 'lodash/fp';

import { getTimeUnitKeys } from '../models/data-key';
import { DATA_QUERY_TYPE } from '../models/data-query';
import { areCompatibleDataTypes } from '../models/data-type';
import {
    AiChatAppliedFilterSet,
    AiChatFilterSet,
    DataFilters,
    DataGranularityKey,
    DataValue,
} from '../types';

const normalizeFilterValues = (values: DataValue | undefined): string[] =>
    Array.isArray(values) ? values.map(String).sort() : [];

const isSameFilterValues = (a: DataValue | undefined, b: DataValue | undefined): boolean =>
    isEqual(normalizeFilterValues(a), normalizeFilterValues(b));

// Data Explorer narrows these to a ranking's result, so they aren't compared against it.
export const getUncomparedFilterKeys = ({ queryType, params }: AiChatAppliedFilterSet): string[] =>
    queryType === DATA_QUERY_TYPE.RANKING ? compact([...getTimeUnitKeys(), params?.rank_by]) : [];

export const getFiltersDiff = (
    current: DataFilters,
    applied: DataFilters,
    uncomparedKeys: string[] = []
): DataFilters =>
    Object.fromEntries(
        (uniq([...Object.keys(current), ...Object.keys(applied)]) as DataGranularityKey[])
            .filter(
                (key) =>
                    !uncomparedKeys.includes(key) && !isSameFilterValues(current[key], applied[key])
            )
            .map((key) => [key, current[key] ?? []])
    );

// Falls back to all filters when no change has a chip to show.
export const getManualFiltersChips = (
    filters: DataFilters,
    changes: DataFilters | null
): DataFilters => {
    const changedValues = omitBy(isEmpty, changes ?? {}) as DataFilters;

    return isEmpty(changedValues) ? filters : changedValues;
};

const getManualFiltersMessageChips = (
    manual: AiChatFilterSet,
    previous: AiChatAppliedFilterSet | null
): DataFilters =>
    getManualFiltersChips(
        manual.filters,
        previous && areCompatibleDataTypes(previous.dataTypes, manual.dataTypes)
            ? getFiltersDiff(manual.filters, previous.filters, getUncomparedFilterKeys(previous))
            : null
    );

export const getThreadManualFiltersChips = <Message>(
    messages: Message[],
    getAppliedFilters: (message: Message) => AiChatAppliedFilterSet | null,
    isManual: (message: Message) => boolean
): Map<Message, DataFilters> => {
    const chips = new Map<Message, DataFilters>();
    let previous: AiChatAppliedFilterSet | null = null;

    messages.forEach((message) => {
        const applied = getAppliedFilters(message);

        if (!applied) {
            return;
        }

        if (isManual(message)) {
            chips.set(message, getManualFiltersMessageChips(applied, previous));
        }

        previous = applied;
    });

    return chips;
};
